// ────────────────────────────────────────────────────────────────────
// Display formatting for the asset integrity screen.
//
// FORMATTING ONLY. Nothing here decides anything: no rate is derived, no
// interval halved or capped, no due date built, no two rates compared. Those
// answers arrive from `GET /api/assets/{id}/integrity` already made, with the
// sentence that explains them. See ./types.ts.
//
// Values are carried at full precision from the wire and rounded at the last
// possible moment — here, for display — so no printed number has been through
// an intermediate rounding step.
// ────────────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const EMDASH = "—";

/** A thickness in mm, two decimals. `null` reads as an em dash, never 0.00 —
 *  "no reading" and "zero wall" must not print the same. */
export function mm(v: number | null | undefined, dp = 2): string {
  return v == null ? EMDASH : v.toFixed(dp);
}

/** A corrosion rate. Three decimals: the handoff's own sentences quote rates
 *  to three (`0.245 mm/yr`), and TOL is 0.0005, so two would render two
 *  distinguishable rates identically. */
export function rateMmYr(v: number | null | undefined): string {
  return v == null ? "no rate" : `${v.toFixed(3)} mm/yr`;
}

export function years(v: number | null | undefined, dp = 2): string {
  return v == null ? EMDASH : `${v.toFixed(dp)} yr`;
}

/** Whole months as the interval a person reads: "60 mo · 5.0 yr".
 *  The months are the server's floored value and are NOT recomputed; the years
 *  beside them are a convenience rendering of the same number. */
export function interval(months: number | null | undefined): string {
  if (months == null) return EMDASH;
  return `${months} mo · ${(months / 12).toFixed(1)} yr`;
}

/** "Mar 2030" — UTC deliberately. An ISO date parses as UTC midnight, and
 *  reading it back in a timezone behind UTC shifts the label into the previous
 *  month: the same class of defect as rounding a due date, which put 6-P-1140
 *  a month late in the issued report (design_handoff/README.md audit item 1). */
export function monthYear(iso: string | null | undefined): string {
  if (!iso) return EMDASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EMDASH;
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** The ISO date as recorded. Inspection dates are printed as filed, not
 *  localised — the report they came from says this string. */
export function isoDay(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : EMDASH;
}

/** The origin vocabulary as a person reads it. Mapping only — the precedence
 *  that ORDERS these lives in `rules.ORIGINS` and the wire arrives sorted. */
export function originLabel(origin: string): string {
  switch (origin) {
    case "recorded":     return "RECORDED";
    case "measured":     return "MEASURED";
    case "profile":      return "PROFILE";
    case "code_default": return "CODE DEFAULT";
    default:             return origin.toUpperCase();
  }
}
